import type { ReactNode } from 'react';
// mui
import { Box, Grid, Stack, Typography } from '@mui/material';
// components
import { RightSidebarTitle } from '..';
import { ChatStream } from '@/features/chat-stream/pages';
import { useChatClient } from '@/hooks/useChatClient';
import { STREAM_CHAT_ENABLED, STREAM_KEY } from '@/config';
import { ChannelFilters, ChannelOptions, ChannelSort, User } from 'stream-chat';
import { LoadingIndicator } from 'stream-chat-react';
import useAuth from '@/hooks/useAuth';

const RightChatPanel = ({ children }: { children: ReactNode }) => (
  <Grid
    item
    container
    direction="column"
    spacing={2}
    sx={{
      m: 0,
      backgroundColor: 'background.paper',
      width: '100%',
      height: '100vh',
      zIndex: 5,
      boxShadow: '-12px 12px 32px - 4px rgba(99, 115, 129, 0.36)',
    }}
  >
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <RightSidebarTitle title="Chat" />
      {children}
    </Box>
  </Grid>
);

export default function RightChatBar() {
  const { user } = useAuth();

  const apiKey = STREAM_KEY;
  const userToken = user?.chat_token || '';

  const userId = user?.chat_user_id;
  const userName = user?.displayName;

  const chatUser: User = {
    id: userId,
    name: userName,
    image: user?.profile_image,
  };

  const chatClient = useChatClient({
    apiKey,
    enabled: STREAM_CHAT_ENABLED && Boolean(userToken) && Boolean(chatUser.id),
    user: chatUser,
    tokenOrProvider: userToken,
  });

  if (!STREAM_CHAT_ENABLED) {
    return (
      <RightChatPanel>
        <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
          <Typography color="text.secondary">
            Stream chat is disabled.
          </Typography>
        </Stack>
      </RightChatPanel>
    );
  }

  if (!chatClient) {
    return (
      <RightChatPanel>
        <LoadingIndicator />
      </RightChatPanel>
    );
  }

  const sort: ChannelSort = { last_message_at: -1 };
  const filters: ChannelFilters = {
    type: {
      $in: [
        'messaging',
        'team',
        'jobSupport',
        'cleanerSupport',
        'clientSupport',
      ],
    },
    members: { $in: [userId] },
  };
  const options: ChannelOptions = {
    limit: 10,
  };

  return (
    <RightChatPanel>
      <Box sx={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
        <ChatStream
          chatClient={chatClient}
          channelSort={sort}
          channelFilters={filters}
          channelOptions={options}
        />
      </Box>
    </RightChatPanel>
  );
}
